import cx from 'classnames';

import BreakpointsContainer from '~/components/BreakpointsContainer';
import PostDetails from '~/components/PostCardv3/PostDetails';
import PostImage from '~/components/PostCardv3/PostImage';
import PostLinkHelper from '~/components/PostCardv3/PostLinkHelper';
import PostPremiumBadge from '~/components/PostCardv3/PostPremiumBadge';
import PostTags from '~/components/PostCardv3/PostTags';
import PostTitles from '~/components/PostCardv3/PostTitles';
import Signup from '~/components/Signup';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

const getHeaderPosts = (posts: any[], featuredPosts: any[]) => {
  let headliner: any;
  const rightPanelPosts: any[] = [];

  featuredPosts.forEach((post, index) => {
    if (index === 0) {
      headliner = post;
    } else {
      rightPanelPosts.push(post);
    }
  });

  // Ensure that the posts have been properly filled out for aesthetic by falling back to regular posts
  let rightPanelPostsCount = rightPanelPosts.length;
  posts.forEach((post) => {
    if (!headliner) {
      headliner = post;
      return;
    }

    if (rightPanelPostsCount >= 4) {
      return;
    }

    rightPanelPosts.push(post);
    rightPanelPostsCount++;
  });

  return {
    headliner,
    rightPanelPosts,
  };
};

const Headliner = () => {
  const { homePage, featuredPosts, paginatedPosts, cardStyles, signupWidgetConfigs } = useTypedLoaderData<any>();

  const { headliner, rightPanelPosts } = getHeaderPosts(paginatedPosts.posts, featuredPosts);

  return (
    <BreakpointsContainer className={cx('mx-auto py-4')}>
      <div className="flex flex-col gap-0 md:flex-row md:gap-8">
        <div className="w-full md:w-3/5">
          <div className="flex flex-col">
            {headliner && (
              <div className="relative">
                <PostLinkHelper
                  slug={headliner?.slug}
                  className={cx('group relative flex w-full flex-col overflow-hidden rounded shadow-xl')}
                >
                  <PostImage
                    alt={headliner.web_title}
                    radiusClass={''}
                    hasRadiusApplied={true}
                    src={headliner.image_url}
                  />
                  <div className="absolute bottom-0 flex h-full w-full flex-col justify-end space-y-1 bg-black bg-opacity-10 p-4 group-hover:bg-opacity-0">
                    <div className="max-w-md space-y-4">
                      {cardStyles?.tags_enabled && (
                        <PostTags className="relative" tags={headliner.content_tags} cardStyles={cardStyles} />
                      )}
                      <PostTitles
                        title={{
                          text: headliner.web_title,
                          fontFamily: cardStyles.heading_font_family,
                          color: '#ffffff',
                        }}
                        subtitle={{
                          text: headliner?.web_subtitle || '',
                          fontFamily: cardStyles.description_font_family,
                          color: '#ffffff',
                        }}
                      />
                    </div>
                  </div>
                </PostLinkHelper>
              </div>
            )}
            <div className="py-4">
              <Signup signupStyles={signupWidgetConfigs} />
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col space-y-4 md:w-2/5">
          {rightPanelPosts.map((post, _index) => {
            const isPremium = post.audience === 'premium';

            return (
              <div key={post.id} className="flex flex-col space-y-2">
                {isPremium && (
                  <PostPremiumBadge
                    fontFamily={cardStyles.font}
                    bgColor={cardStyles.premium_bg_color}
                    color={cardStyles.premium_text_color}
                  />
                )}
                <PostLinkHelper
                  key={post.id}
                  slug={post?.slug}
                  className={cx('group relative flex w-full flex-col space-y-2 transition-all')}
                >
                  <PostTitles
                    title={{
                      text: post.web_title,
                      fontFamily: homePage?.general_section?.theme?.font,
                      color: homePage?.general_section?.theme?.text_color,
                      size: 'xl',
                    }}
                    subtitle={{
                      text: post?.web_subtitle || '',
                      fontFamily: homePage?.general_section?.theme?.font,
                      color: homePage?.general_section?.theme?.text_color,
                      size: 'sm',
                    }}
                  />
                </PostLinkHelper>
                <div className="flex flex-wrap-reverse items-center justify-between">
                  {cardStyles.timefields_enabled && (
                    <PostDetails
                      fontFamily={cardStyles.font}
                      color={cardStyles.timestamp_text_color}
                      timestamp={post.override_scheduled_at}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </BreakpointsContainer>
  );
};

export default Headliner;
